import React, { useState, useEffect } from "react";
import axios from "../../utils/axiosInstance";
import { FaFilter } from "react-icons/fa";
import { toastError } from "../../utils/alertHelper";
import CommonTable from "../../components/commonComponent/CommanTable";
import CommanFilters from "../../components/commonComponent/CommanFilters";

const tabs = ["Due Today", "Upcoming", "Overdue", "Never FollowUp"];

// Tab configuration with proper mapping
const tabConfig = [
  { key: "Due Today", label: "Due Today" },
  { key: "Upcoming", label: "Upcoming" },
  { key: "Overdue", label: "Overdue" },
  { key: "Never FollowUp", label: "Never FollowUp" }
];

// Tab hover styles
const tabHoverStyle = {
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  border: '1px solid transparent'
};

export default function FollowupTable() {
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [search, setSearch] = useState("");
  const [followups, setFollowups] = useState([]);
  const [filteredFollowups, setFilteredFollowups] = useState([]); // 🔍 for filtered list
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
  });

  const businessId = localStorage.getItem("businessId") || "";

  useEffect(() => {
    fetchFollowups();
  }, [businessId, activeTab, filters]);

  useEffect(() => {
    handleSearch();
  }, [search, followups]);

  const fetchFollowups = async () => {
    try {
      setLoading(true);
      const params = {
        business: businessId,
        followup: activeTab.toLowerCase(),
      };

      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await axios.get("/admin/api/lead-followup", { params });

      if (response.data && response.data.Status === 1) {
        setFollowups(response.data.followups || []);
        setFilteredFollowups(response.data.followups || []);
        setTotalCount(response.data.totalCount || 0);
      } else {
        setFollowups([]);
        setFilteredFollowups([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching followups:", error);
      toastError("Failed to fetch followups");
      setFollowups([]);
      setFilteredFollowups([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  //  Frontend search by lead name
  const handleSearch = () => {
    const value = search.trim().toLowerCase();
    if (!value) {
      setFilteredFollowups(followups);
    } else {
      const filtered = followups.filter((f) =>
        f.lead?.name?.toLowerCase().includes(value)
      );
      setFilteredFollowups(filtered);
    }
  };

  const handleSearchChange = (e) => setSearch(e.target.value);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyFilters = () => fetchFollowups();
  const handleClearFilters = () => setFilters({ startDate: "", endDate: "" });
  const toggleFilters = () => setShowFilters(!showFilters);

  const columns = [
    { key: "srno", label: "Sr.no" },
    { key: "name", label: "Name" },
    { key: "followUpDate", label: "FollowUp Date" },
    { key: "lastActivity", label: "Last Activity" },
    { key: "stage", label: "Stage" },
    { key: "types", label: "Types" },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      " (" +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) +
      ")"
    );
  };

  const tableData = filteredFollowups.map((followup, idx) => ({
    srno: idx + 1,
    name: followup.lead?.name || "N/A",
    followUpDate: formatDate(followup.nextdate),
    lastActivity: formatDate(followup.updatedAt || followup.createdAt),
    stage: followup.lead?.dealStage?.name || "N/A",
    types: followup.lead?.dealType?.name || "N/A",
  }));

  return (
    <div className="container-fluid mt-4">
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="fw-bold mb-0 mt-2">Follow Up</h2>

        {/* Filter Button */}
        <button
          className="btn btn-light d-flex align-items-center"
          onClick={toggleFilters}
          style={{ gap: "6px", borderRadius: "6px" }}
        >
          <FaFilter /> <span>Filter</span>
        </button>
      </div>

      {/* Filters Section */}
      {showFilters && (
        <CommanFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          fields={["dateRange"]}
          dropdowns={{}}
        />
      )}

      {/* Tabs & Search on Same Line */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 nav-tabs">
        <ul className="nav  mb-0">
          {tabConfig.map(tab => (
            <li className="nav-item" key={tab.key}>
              <button
                className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                style={{
                  ...tabHoverStyle,
                  backgroundColor: activeTab === tab.key ? '#5766A1' : '',
                  color: activeTab === tab.key ? '#ffffff' : '#212529',
                  border: activeTab === tab.key ? '1px solid #5766A1' : '1px solid transparent',
                  fontWeight: activeTab === tab.key ? '600' : '500'
                }}
                onMouseEnter={e => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.background = '#e9ecef';
                    e.currentTarget.style.color = '#5766A1';
                  }
                }}
                onMouseLeave={e => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.background = '';
                    e.currentTarget.style.color = '#212529';
                  }
                }}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label} {activeTab === tab.key && `(${totalCount})`}
              </button>
            </li>
          ))}
        </ul>

        {/* 🔍 Search Box */}
        <div
          className="input-group mb-3"
          style={{
            maxWidth: 280,
            borderRadius: "6px",
            overflow: "hidden",
          }}
        >
          <span
            className="input-group-text bg-white border-end-0"
            style={{ borderRadius: "6px 0 0 6px" }}
          >
            <i className="fas fa-search text-muted"></i>
          </span>
          <input
            type="search"
            className="form-control border-start-0"
            placeholder="Search by name..."
            value={search}
            onChange={handleSearchChange}
            style={{ boxShadow: "none", borderRadius: "0 6px 6px 0" }}
          />
        </div>
      </div>

      {/* Table Section */}
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <CommonTable
          columns={columns}
          data={tableData}
          itemsPerPageOptions={[5, 10, 15, 25, 50]}
          defaultItemsPerPage={15}
          showPagination={true}
        />
      )}
    </div>
  );
}
